import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    getDashboard,
    getTenants,
    getTenant,
    getLicenseSummary,
    updateTenant,
    activateTenant,
    suspendTenant,
    getInvoices,
    getInvoice,
    markInvoicePaid,
    getDnsStatus,
    verifyDns,
    enroll,
    login,
    changePlan,
    getTenantUsers,
    createTenantUser,
    resetTenantUserPassword,
    deleteTenantUser,
    getIdentityTenants,
    setIdentityLink,
    type TenantStatus,
    type InvoiceStatus,
    type UpdateTenantRequest,
    type EnrollRequest,
    type ChangePlanRequest,
    type CreateTenantUserRequest
} from "./portalApi";
import { useAuth } from "@/auth/AuthContext";

// ── Queries ──────────────────────────────────────────────────────────────────

export function useDashboard() {
    const { token } = useAuth();
    return useQuery({
        queryKey: ["portal", "dashboard"],
        queryFn: () => getDashboard(token),
        enabled: Boolean(token)
    });
}

export function useTenants(status?: TenantStatus) {
    const { token } = useAuth();
    return useQuery({
        queryKey: ["portal", "tenants", status],
        queryFn: () => getTenants(token, status),
        enabled: Boolean(token)
    });
}

export function useTenant(id: string | undefined) {
    const { token } = useAuth();
    return useQuery({
        queryKey: ["portal", "tenant", id],
        queryFn: () => getTenant(token, id!),
        enabled: Boolean(token) && Boolean(id)
    });
}

export function useLicenseSummary(id: string | undefined) {
    const { token } = useAuth();
    return useQuery({
        queryKey: ["portal", "tenant", id, "license"],
        queryFn: () => getLicenseSummary(token, id!),
        enabled: Boolean(token) && Boolean(id)
    });
}

export function useInvoices(status?: InvoiceStatus, tenantId?: string) {
    const { token } = useAuth();
    return useQuery({
        queryKey: ["portal", "invoices", status, tenantId],
        queryFn: () => getInvoices(token, status, tenantId),
        enabled: Boolean(token)
    });
}

export function useInvoice(id: string | undefined) {
    const { token } = useAuth();
    return useQuery({
        queryKey: ["portal", "invoice", id],
        queryFn: () => getInvoice(token, id!),
        enabled: Boolean(token) && Boolean(id)
    });
}

export function useDnsStatus(tenantId: string | undefined) {
    const { token } = useAuth();
    return useQuery({
        queryKey: ["portal", "dns", tenantId],
        queryFn: () => getDnsStatus(token, tenantId!),
        enabled: Boolean(token) && Boolean(tenantId)
    });
}

// ── Mutations ────────────────────────────────────────────────────────────────

export function useLogin() {
    return useMutation({
        mutationFn: ({ username, password }: { username: string; password: string }) =>
            login(username, password)
    });
}

export function useEnroll() {
    return useMutation({
        mutationFn: (data: EnrollRequest) => enroll(data)
    });
}

export function useMarkInvoicePaid() {
    const qc = useQueryClient();
    const { token } = useAuth();
    return useMutation({
        mutationFn: (id: string) => markInvoicePaid(token, id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["portal", "invoices"] });
            void qc.invalidateQueries({ queryKey: ["portal", "dashboard"] });
        }
    });
}

export function useActivateTenant() {
    const qc = useQueryClient();
    const { token } = useAuth();
    return useMutation({
        mutationFn: (id: string) => activateTenant(token, id),
        onSuccess: (_data, id) => {
            void qc.invalidateQueries({ queryKey: ["portal", "tenants"] });
            void qc.invalidateQueries({ queryKey: ["portal", "tenant", id] });
            void qc.invalidateQueries({ queryKey: ["portal", "dashboard"] });
        }
    });
}

export function useSuspendTenant() {
    const qc = useQueryClient();
    const { token } = useAuth();
    return useMutation({
        mutationFn: (id: string) => suspendTenant(token, id),
        onSuccess: (_data, id) => {
            void qc.invalidateQueries({ queryKey: ["portal", "tenants"] });
            void qc.invalidateQueries({ queryKey: ["portal", "tenant", id] });
            void qc.invalidateQueries({ queryKey: ["portal", "dashboard"] });
        }
    });
}

export function useUpdateTenant() {
    const qc = useQueryClient();
    const { token } = useAuth();
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: UpdateTenantRequest }) =>
            updateTenant(token, id, data),
        onSuccess: (_data, { id }) => {
            void qc.invalidateQueries({ queryKey: ["portal", "tenant", id] });
            void qc.invalidateQueries({ queryKey: ["portal", "tenants"] });
        }
    });
}

export function useChangePlan() {
    const qc = useQueryClient();
    const { token } = useAuth();
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: ChangePlanRequest }) =>
            changePlan(token, id, data),
        onSuccess: (_data, { id }) => {
            void qc.invalidateQueries({ queryKey: ["portal", "tenant", id] });
            void qc.invalidateQueries({ queryKey: ["portal", "tenants"] });
            void qc.invalidateQueries({ queryKey: ["portal", "license", id] });
            void qc.invalidateQueries({ queryKey: ["portal", "invoices"] });
        }
    });
}

export function useIdentityTenants() {
    const { token } = useAuth();
    return useQuery({
        queryKey: ["portal", "identity-tenants"],
        queryFn: () => getIdentityTenants(token),
        enabled: Boolean(token),
        staleTime: 60_000
    });
}

export function useSetIdentityLink() {
    const qc = useQueryClient();
    const { token } = useAuth();
    return useMutation({
        mutationFn: ({ portalTenantId, identityTenantId }: { portalTenantId: string; identityTenantId: string | null }) =>
            setIdentityLink(token, portalTenantId, identityTenantId),
        onSuccess: (_data, { portalTenantId }) => {
            void qc.invalidateQueries({ queryKey: ["portal", "tenant", portalTenantId] });
        }
    });
}

export function useTenantUsers(tenantId: string | undefined) {
    const { token } = useAuth();
    return useQuery({
        queryKey: ["portal", "tenant", tenantId, "users"],
        queryFn: () => getTenantUsers(token, tenantId!),
        enabled: Boolean(token) && Boolean(tenantId)
    });
}

export function useCreateTenantUser() {
    const qc = useQueryClient();
    const { token } = useAuth();
    return useMutation({
        mutationFn: ({ tenantId, data }: { tenantId: string; data: CreateTenantUserRequest }) =>
            createTenantUser(token, tenantId, data),
        onSuccess: (_data, { tenantId }) => {
            void qc.invalidateQueries({ queryKey: ["portal", "tenant", tenantId, "users"] });
            void qc.invalidateQueries({ queryKey: ["portal", "tenant", tenantId, "license"] });
        }
    });
}

export function useResetTenantUserPassword() {
    const { token } = useAuth();
    return useMutation({
        mutationFn: ({ tenantId, userId, newPassword }: { tenantId: string; userId: string; newPassword: string }) =>
            resetTenantUserPassword(token, tenantId, userId, newPassword)
    });
}

export function useDeleteTenantUser() {
    const qc = useQueryClient();
    const { token } = useAuth();
    return useMutation({
        mutationFn: ({ tenantId, userId }: { tenantId: string; userId: string }) =>
            deleteTenantUser(token, tenantId, userId),
        onSuccess: (_data, { tenantId }) => {
            void qc.invalidateQueries({ queryKey: ["portal", "tenant", tenantId, "users"] });
            void qc.invalidateQueries({ queryKey: ["portal", "tenant", tenantId, "license"] });
        }
    });
}

export function useVerifyDns() {
    const qc = useQueryClient();
    const { token } = useAuth();
    return useMutation({
        mutationFn: (tenantId: string) => verifyDns(token, tenantId),
        onSuccess: (data, tenantId) => {
            // Populate the DNS cache with the live verification result so the
            // table shows actual pass/fail per record immediately — don't just
            // invalidate, which would refetch the static GET (always Unknown).
            qc.setQueryData(["portal", "dns", tenantId], data);
            void qc.invalidateQueries({ queryKey: ["portal", "tenants"] });
            void qc.invalidateQueries({ queryKey: ["portal", "tenant", tenantId] });
        }
    });
}
